import { useEffect, useRef, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Checkbox } from '@opendoor/atoms/checkbox';
import { Combobox } from '@opendoor/atoms/combobox';
import type { ComboboxOption } from '@opendoor/atoms/combobox';
import { Dialog } from '@opendoor/atoms/dialog';
import { Field } from '@opendoor/atoms/field';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import { globalObservability } from '@opendoor/observability/slim';
import { SellerAddressConfirmation } from '@opendoor/shared-fe/seller-address-confirmation';
import { useRouter } from 'next/router';

import type { SellerLeadChannel } from '../../__generated__/zeus/types';
import type { SellerAddressFlow } from '../../helpers/addressValidation';
import {
  ADDRESS_INVALID_RESULT,
  gateProceedToOnboardingFlow,
  useAddressValidationContext,
} from '../../helpers/AddressValidationContext';
import type { SelectedAddress } from '../../helpers/AddressValidationContext';
import { rememberManualAddressHandoff } from '../../helpers/manualAddressDraft';
import { useObservability } from '../../helpers/observability';
import { redactGraphqlRequestErrorForTelemetry } from '../api/graphqlRequestError';
import { impressOnboardingZeroAddress, isOnboardingZeroAddress } from '../api/onboardingZeroArm';
import { standardizeSellerAddress } from '../api/sellerAddressIntake';
import type { SellerOwnerRelationshipAnswer } from '../api/sellerAddressIntake';
import { isMobileAppWebView } from '../api/sellerLeadCakeRollout';
import type { TrackingTaxonomy } from '../globals';
import { extractStreetNumber } from './AddressConfirmationModal.helpers';

const Sentry = globalObservability.getSentryClient();

const RELATIONSHIP_OPTIONS: Array<{ value: SellerOwnerRelationshipAnswer; label: string }> = [
  { value: 'Self', label: 'Yes, I own this home' },
  { value: 'Agent', label: 'No, I’m an agent' },
  { value: 'AgentAndOwner', label: 'I’m an agent, and own this home' },
  { value: 'other', label: 'Other' },
];

export const AddressConfirmationModal = ({
  isOpen,
  sellerFlow,
  setIsOpen,
  partnerReferralId,
  initialAddress,
  pageSetPath,
  trackingKeywords,
  channel,
  trackingTaxonomy,
  originSurface,
  onHandoffPendingChange,
  confirmationMode = 'relationship-select',
}: {
  confirmationMode?: 'relationship-select' | 'owner-first';
  sellerFlow?: SellerAddressFlow;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  partnerReferralId?: string;
  initialAddress: SelectedAddress;
  pageSetPath?: string;
  trackingKeywords?: string;
  channel?: SellerLeadChannel;
  trackingTaxonomy: TrackingTaxonomy;
  originSurface?: string;
  /** Remains true through navigation after a skip. */
  onHandoffPendingChange?: (pending: boolean) => void;
}) => {
  const router = useRouter();
  // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
  const { trackEvent, trackJourneyEvent } = useObservability();
  const { addressResults, setValidatedAddress, validatedAddress } = useAddressValidationContext();
  const [address, setAddress] = useState<SelectedAddress>({ ...initialAddress });
  const [unitNumberErrorText, setUnitNumberErrorText] = useState<string>('');
  const [submissionError, setSubmissionError] = useState('');
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [noUnitChecked, setNoUnitChecked] = useState<boolean>(false);
  const [confirmSameUnitStreet, setConfirmSameUnitStreet] = useState<boolean>(false);
  const [relationshipValue, setRelationshipValue] = useState<SellerOwnerRelationshipAnswer | ''>(
    '',
  );
  const [showOtherRelationships, setShowOtherRelationships] = useState(false);
  const relationshipInputRef = useRef<HTMLInputElement>(null);
  const [relationshipError, setRelationshipError] = useState<string>('');
  const isAddressUnitNumberMissing = addressResults?.includes('ADDRESS_MISSING_SUB_PREMISE');
  // A missing unit keeps the modal: adding the unit in Cake changes the address and so the arm.
  // The app WebView stays on Reception, which has no Onboarding Zero flow, so it keeps the modal too.
  const armAddressId =
    !isAddressUnitNumberMissing && initialAddress.id && !isMobileAppWebView()
      ? initialAddress.id
      : null;
  const [armDecision, setArmDecision] = useState<{ addressId: string; skip: boolean }>();
  // Undefined until the arm read settles; the modal waits so it does not flash before a skip.
  let skipModal: boolean | undefined = false;
  if (armAddressId !== null) {
    skipModal = armDecision?.addressId === armAddressId ? armDecision.skip : undefined;
  }
  const handoffPending = isOpen && skipModal !== false;
  const skippedAddresses = useRef(new Set<string>());
  const activeAttempt = useRef<AbortController | null>(null);
  const streetNumber = extractStreetNumber(initialAddress);
  const showOwnerConfirmation =
    confirmationMode === 'owner-first' && !isAddressUnitNumberMissing && !showOtherRelationships;

  useEffect(() => {
    if (showOtherRelationships) relationshipInputRef.current?.focus();
  }, [showOtherRelationships]);

  const validateUnitInput = async (signal: AbortSignal): Promise<SelectedAddress | undefined> => {
    try {
      const standardizedAddress = await (
        sellerFlow?.standardizeAddress ?? standardizeSellerAddress
      )(address);
      if (signal.aborted) return undefined;
      if (!standardizedAddress) {
        setUnitNumberErrorText('Unit number not found or entered incorrectly');
        return undefined;
      }
      if (
        standardizedAddress.validationResults.some((result) =>
          ADDRESS_INVALID_RESULT.includes(result),
        )
      ) {
        setUnitNumberErrorText('Unit number not found or entered incorrectly');
        return undefined;
      }
      return {
        street1: standardizedAddress.street,
        city: standardizedAddress.city,
        state: standardizedAddress.state,
        postal_code: standardizedAddress.postalCode,
        unit: standardizedAddress.unitNumber?.trim() ?? '',
        id: standardizedAddress.id,
      };
    } catch (error) {
      if (signal.aborted) return undefined;
      Sentry.captureException?.(redactGraphqlRequestErrorForTelemetry(error));
      setUnitNumberErrorText('An unexpected error occurred. Please try again.');
      return undefined;
    }
  };

  const routeToManualAddressPage = () => {
    const searchSessionId =
      new URLSearchParams(window.location.search).get('searchSessionId') ?? undefined;
    if (validatedAddress) {
      rememberManualAddressHandoff({
        searchSessionId,
        fields: {
          street: validatedAddress.street1 ?? '',
          unit: validatedAddress.unit ?? '',
          city: validatedAddress.city ?? '',
          state: validatedAddress.state ?? '',
          postalCode: validatedAddress.postal_code ?? '',
        },
      });
    }
    void router.push({
      pathname: '/manual-address-entry',
      query: {
        ...(trackingKeywords != null && { trackingKeywords }),
        ...(pageSetPath != null && { pageSetPath }),
        ...(partnerReferralId != null && { partnerReferralId }),
        ...(channel != null && { channel }),
        ...(originSurface != null && { originSurface }),
        ...(searchSessionId ? { searchSessionId } : {}),
      },
    });
  };

  const releaseAttempt = (attempt: AbortController) => {
    if (activeAttempt.current !== attempt) return;
    activeAttempt.current = null;
    setIsConfirmLoading(false);
  };

  const proceedWithAddress = async ({
    addressToUse,
    signal,
    relationship = relationshipValue || undefined,
  }: {
    addressToUse: SelectedAddress;
    signal: AbortSignal;
    relationship?: SellerOwnerRelationshipAnswer;
  }): Promise<boolean> => {
    if (signal.aborted) return false;
    setSubmissionError('');
    setValidatedAddress(addressToUse);

    try {
      await (sellerFlow?.proceed ?? gateProceedToOnboardingFlow)({
        signal,
        address: addressToUse,
        trackEvent,
        partnerReferralId,
        addressUUID: addressToUse.id,
        pageSetPath,
        trackingKeywords,
        channel,
        trackingTaxonomy,
        relationship: isAddressUnitNumberMissing ? undefined : relationship,
        originSurface,
      });
      return !signal.aborted;
    } catch (error) {
      if (signal.aborted) return false;
      Sentry.captureException?.(redactGraphqlRequestErrorForTelemetry(error));
      setSubmissionError('An unexpected error occurred. Please try again.');
      return false;
    }
  };

  const validateRelationship = (relationship: SellerOwnerRelationshipAnswer | ''): boolean => {
    if (!relationship) {
      setRelationshipError('Please select an option');
      return false;
    }
    setRelationshipError('');
    return true;
  };

  const handleRelationshipSelect = (value: SellerOwnerRelationshipAnswer | '') => {
    setRelationshipValue(value);
    setRelationshipError('');
  };

  const handleNoUnitToggle = (checked: boolean) => {
    setNoUnitChecked(checked);
    if (checked) {
      setAddress((prev) => ({ ...prev, unit: '' }));
      setUnitNumberErrorText('');
    }
  };

  const handleConfirm = async (
    relationship: SellerOwnerRelationshipAnswer | '' = relationshipValue,
  ) => {
    if (activeAttempt.current) return;
    const attempt = new AbortController();
    activeAttempt.current = attempt;
    setIsConfirmLoading(true);

    try {
      if (!isAddressUnitNumberMissing && !validateRelationship(relationship)) return;

      if (isAddressUnitNumberMissing && !noUnitChecked) {
        if (!address.unit?.trim()) {
          setUnitNumberErrorText('Enter a unit number or check "No unit number" below');
          return;
        }

        const validatedAddr = await validateUnitInput(attempt.signal);
        if (validatedAddr) {
          await proceedWithAddress({ addressToUse: validatedAddr, signal: attempt.signal });
        }
      } else {
        await proceedWithAddress({
          addressToUse: address,
          signal: attempt.signal,
          relationship: relationship || undefined,
        });
      }
    } finally {
      releaseAttempt(attempt);
    }
  };

  const handleUnitNumberChange = (input: string) => {
    setConfirmSameUnitStreet(false);
    if (address) {
      setAddress({ ...address, unit: input });
    }
    const validUnitNumberRegex = /^[A-Za-z0-9\s-#]+$/;
    if (input && !validUnitNumberRegex.test(input)) {
      setUnitNumberErrorText('Unit numbers can’t include special characters.');
    } else if (input === streetNumber) {
      setUnitNumberErrorText(
        'If street and unit number are the same, tap "Same street and unit" below.',
      );
    } else {
      setUnitNumberErrorText('');
    }
  };

  useEffect(() => {
    if (!isOpen || armAddressId === null) return undefined;
    let current = true;
    void isOnboardingZeroAddress(armAddressId).then((skip) => {
      if (current) {
        setArmDecision({ addressId: armAddressId, skip });
        void impressOnboardingZeroAddress({ addressId: armAddressId, trackJourneyEvent });
      }
      return skip;
    });
    return () => {
      current = false;
    };
  }, [armAddressId, isOpen, trackJourneyEvent]);

  useEffect(() => {
    onHandoffPendingChange?.(handoffPending);
  }, [handoffPending, onHandoffPendingChange]);

  // Onboarding Zero has no address or owner step, so the handoff confirms the address on its own.
  useEffect(() => {
    if (!isOpen || !skipModal || !armAddressId || skippedAddresses.current.has(armAddressId))
      return;
    skippedAddresses.current.add(armAddressId);
    const attempt = new AbortController();
    activeAttempt.current = attempt;
    setIsConfirmLoading(true);
    void proceedWithAddress({ addressToUse: initialAddress, signal: attempt.signal })
      .then((started) => {
        if (attempt.signal.aborted) return;
        // A failed skip shows the modal with its error, so the seller can retry or edit.
        if (!started && armAddressId) setArmDecision({ addressId: armAddressId, skip: false });
      })
      .finally(() => releaseAttempt(attempt));
    // oxlint-disable-next-line react/exhaustive-deps -- Each address hands off once; the in-flight handoff owns its values.
  }, [armAddressId, isOpen, skipModal]);

  useEffect(() => {
    const restore = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      activeAttempt.current?.abort();
      activeAttempt.current = null;
      skippedAddresses.current.clear();
      setRelationshipValue('');
      setShowOtherRelationships(false);
      setRelationshipError('');
      setSubmissionError('');
      setUnitNumberErrorText('');
      setIsConfirmLoading(false);
      setIsOpen(false);
      onHandoffPendingChange?.(false);
    };
    window.addEventListener('pageshow', restore);
    return () => window.removeEventListener('pageshow', restore);
  }, [onHandoffPendingChange, setIsOpen]);

  useEffect(
    () => () => {
      activeAttempt.current?.abort();
      activeAttempt.current = null;
    },
    [initialAddress.id],
  );

  const handleSameStreetUnit = () => {
    setUnitNumberErrorText('');
    setConfirmSameUnitStreet(true);
  };

  const handleRelationshipOptionChange = (option: ComboboxOption | null) => {
    const selected = RELATIONSHIP_OPTIONS.find((candidate) => candidate.value === option?.value);
    handleRelationshipSelect(selected?.value ?? '');
  };

  const handleOwnerFirstConfirm = () => {
    void handleConfirm(showOwnerConfirmation ? 'Self' : relationshipValue);
  };

  const selectedRelationshipOption =
    RELATIONSHIP_OPTIONS.find((option) => option.value === relationshipValue) ?? null;

  if (handoffPending) {
    return (
      <output className="sr-only">
        {skipModal ? 'Opening your home details…' : 'Confirming your address…'}
      </output>
    );
  }

  const addressFields = (
    <>
      {isAddressUnitNumberMissing && (
        <Col className="gap-3">
          <Field
            id="cosmos-address-confirmation-input"
            label="Add unit number, apt, floor, etc."
            error={unitNumberErrorText || undefined}
          >
            <Input
              id="cosmos-address-confirmation-input"
              size="base"
              placeholder="#1234"
              value={address?.unit ?? ''}
              disabled={noUnitChecked}
              error={Boolean(unitNumberErrorText)}
              onChange={(event) => handleUnitNumberChange(event.target.value)}
            />
          </Field>

          {address?.unit === streetNumber && !confirmSameUnitStreet && !noUnitChecked && (
            <Button
              id="same-street-unit"
              aria-label="Same street and unit"
              analyticsName="cosmos-same-street-unit"
              trackImpression
              variant="primary"
              className="h-auto w-max border-0 bg-transparent p-0 text-label-lg !text-od-content-accent-brand not-disabled:hover:bg-transparent not-disabled:active:scale-100"
              onClick={handleSameStreetUnit}
            >
              Same street and unit
            </Button>
          )}

          <Row analyticsName="address-confirmation-no-unit-checkbox" className="items-center gap-2">
            <Checkbox
              analyticsName="address-confirmation-no-unit"
              aria-label="No unit number"
              value="no-unit"
              checked={noUnitChecked}
              onCheckedChange={handleNoUnitToggle}
            />
            <Text as="span" className="text-body-md text-od-content-secondary">
              No unit number
            </Text>
          </Row>
        </Col>
      )}

      {!isAddressUnitNumberMissing && !showOwnerConfirmation && (
        <Field
          id="address-confirmation-relationship"
          label="Are you the owner of this home?"
          error={relationshipError || undefined}
        >
          <Combobox
            ref={relationshipInputRef}
            id="address-confirmation-relationship"
            analyticsName="address-confirmation-relationship-dropdown"
            options={RELATIONSHIP_OPTIONS}
            value={selectedRelationshipOption}
            placeholder="Select"
            error={Boolean(relationshipError)}
            onValueChange={handleRelationshipOptionChange}
          />
        </Field>
      )}
    </>
  );

  if (confirmationMode === 'owner-first') {
    return (
      <SellerAddressConfirmation
        open={isOpen}
        container={
          typeof document === 'undefined'
            ? undefined
            : (document.getElementById('atoms-overlay-root') ?? undefined)
        }
        onOpenChange={setIsOpen}
        title={
          isAddressUnitNumberMissing ? 'Is there a unit?' : 'Is this the correct home address?'
        }
        address={{
          street: `${address.street1}${address.unit ? `, #${address.unit}` : ''}`,
          locality: `${address.city}, ${address.state} ${address.postal_code}`,
        }}
        onEdit={routeToManualAddressPage}
        confirming={isConfirmLoading}
        confirmDisabled={
          (!showOwnerConfirmation &&
            !isAddressUnitNumberMissing &&
            (!relationshipValue || !!relationshipError)) ||
          !!unitNumberErrorText ||
          (!!isAddressUnitNumberMissing && !address.unit?.trim() && !noUnitChecked)
        }
        onConfirm={handleOwnerFirstConfirm}
        onOtherRelationship={
          showOwnerConfirmation ? () => setShowOtherRelationships(true) : undefined
        }
        error={submissionError}
        analyticsNames={{
          edit: 'cosmos-edit-address',
          confirm: showOwnerConfirmation
            ? 'address-confirmation-modal-owner-confirm'
            : 'address-confirmation-modal-confirm',
          other: 'address-confirmation-modal-other-relationship',
        }}
      >
        {!showOwnerConfirmation && <Col className="pt-8">{addressFields}</Col>}
      </SellerAddressConfirmation>
    );
  }

  return (
    <Dialog.Root role="dialog" open={isOpen} onOpenChange={setIsOpen}>
      <Dialog className="sm:w-122 lg:w-148">
        <Dialog.Title className="pb-4 sm:pr-8 sm:pb-6 sm:text-subheader-lg">
          {isAddressUnitNumberMissing ? 'Is there a unit?' : 'Is this the correct home address?'}
        </Dialog.Title>
        <Col className="w-full gap-6">
          <Col className="gap-8 max-lg:gap-6">
            <Row className="w-full items-center justify-between rounded-3xl bg-od-bg-secondary p-6 max-lg:rounded-2xl max-lg:p-4">
              <Col className="w-3/5 max-lg:w-[70%]">
                <Text as="p" className="text-body-xl max-lg:text-body-lg">
                  {address?.street1}
                  {address?.unit ? `, #${address.unit}` : ''}
                </Text>
                <Text as="p" className="text-body-xl max-lg:text-body-lg">
                  {address?.city}, {address?.state} {address?.postal_code}
                </Text>
              </Col>
              <Button
                analyticsName="cosmos-edit-address"
                aria-label="Edit"
                variant="primary"
                className="h-auto w-max border-0 bg-transparent p-0 text-label-lg !text-od-content-accent-brand not-disabled:hover:bg-transparent not-disabled:active:scale-100"
                onClick={routeToManualAddressPage}
              >
                Edit
              </Button>
            </Row>

            {addressFields}
          </Col>

          {submissionError && (
            <Text as="p" role="alert" className="text-body-sm text-od-content-error">
              {submissionError}
            </Text>
          )}
          <Button
            className="w-full"
            analyticsName="address-confirmation-modal-confirm"
            aria-label="Confirm"
            size="lg"
            variant="primary"
            loading={isConfirmLoading}
            disabled={
              (!isAddressUnitNumberMissing && (!relationshipValue || !!relationshipError)) ||
              !!unitNumberErrorText ||
              (isAddressUnitNumberMissing && !address.unit?.trim() && !noUnitChecked)
            }
            onClick={() => handleConfirm()}
          >
            Confirm
          </Button>
        </Col>
      </Dialog>
    </Dialog.Root>
  );
};
